import React, { useEffect, useRef, useState } from 'react';
import {
  BILLING_PERIOD_MONTH_LABELS,
  formatBillingPeriodValue,
  parseBillingPeriodValue,
} from '../../utils/billingPeriodPicker';

interface BillingPeriodMonthPickerProps {
  value: string;
  onChange: (value: string) => void;
  minYear?: number;
  maxYear?: number;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  allowEmpty?: boolean;
  emptyLabel?: string;
}

export const BillingPeriodMonthPicker: React.FC<BillingPeriodMonthPickerProps> = ({
  value,
  onChange,
  minYear = 2000,
  maxYear = new Date().getFullYear() + 1,
  disabled = false,
  className = '',
  placeholder = 'Chọn kỳ',
  allowEmpty = false,
  emptyLabel = 'Tất cả kỳ',
}) => {
  const parsed = value ? parseBillingPeriodValue(value) : null;
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(parsed?.year ?? new Date().getFullYear());
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (parsed) {
      setViewYear(parsed.year);
    }
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const displayValue = value || (allowEmpty ? emptyLabel : placeholder);

  const selectMonth = (month: number) => {
    onChange(formatBillingPeriodValue(month, viewYear));
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm min-w-[140px] disabled:opacity-50 disabled:cursor-not-allowed hover:border-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
      >
        <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <span className={value ? 'text-gray-900' : 'text-gray-500'}>{displayValue}</span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 w-[280px]">
          <div className="flex items-center justify-between mb-3 px-1">
            <button
              type="button"
              onClick={() => setViewYear((y) => Math.max(minYear, y - 1))}
              disabled={viewYear <= minYear}
              className="p-1 text-gray-500 hover:text-gray-800 disabled:opacity-30"
              aria-label="Năm trước"
            >
              «
            </button>
            <span className="font-semibold text-gray-800">{viewYear} Năm</span>
            <button
              type="button"
              onClick={() => setViewYear((y) => Math.min(maxYear, y + 1))}
              disabled={viewYear >= maxYear}
              className="p-1 text-gray-500 hover:text-gray-800 disabled:opacity-30"
              aria-label="Năm sau"
            >
              »
            </button>
          </div>

          <div className="grid grid-cols-4 gap-1">
            {BILLING_PERIOD_MONTH_LABELS.map((label, index) => {
              const month = index + 1;
              const isSelected =
                parsed &&
                parsed.month === month &&
                parsed.year === viewYear &&
                value === formatBillingPeriodValue(month, viewYear);
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => selectMonth(month)}
                  className={`py-2 text-sm rounded-md transition-colors ${
                    isSelected
                      ? 'bg-emerald-600 text-white font-medium'
                      : 'text-gray-700 hover:bg-emerald-50'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {allowEmpty && (
            <button
              type="button"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
              className="mt-3 w-full text-sm text-center text-cyan-600 hover:underline"
            >
              {emptyLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default BillingPeriodMonthPicker;
